import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppWindow, CloudOff, ExternalLink, FolderKanban, History, LoaderCircle, Power, PowerOff, Rocket, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchMarketplaceAppsApi, startMarketplaceAppApi, stopMarketplaceAppApi, readyAppDisplayTitle } from '../../api/marketplaceApps.api';
import { READY_APP_DOMAIN_SUFFIX } from '../../config/readyApps';
import { completeDeploymentApi, fetchDeploymentsApi } from '../../api/sandbox.api';
import { Button } from '../../components/ui/Button';
import { occupiesPublishSlot, hasPublishSlotRemaining } from '../apps/readyAppQuota';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { refreshMyPackage, useMyPackage } from '../../hooks/useMyPackage';
import { fetchProjects } from '../../redux/slices/projectsSlice';
import { classNames } from '../../utils/classNames';
import { ProductDeploymentHistoryModal } from './ProductDeploymentHistoryModal';

const isActiveDeployment = (deployment) =>
  (deployment.status || '').toLowerCase() === 'active' && Boolean(deployment.domain);

const latestDeploymentPerProduct = (deployments) => {
  const latestByProject = new Map();

  deployments.forEach((deployment) => {
    const projectId = deployment.projectId;
    if (!projectId) {
      return;
    }

    const current = latestByProject.get(projectId);
    if (!current) {
      latestByProject.set(projectId, deployment);
      return;
    }

    const currentTime = new Date(current.createdAt || current.updatedAt || 0).getTime();
    const nextTime = new Date(deployment.createdAt || deployment.updatedAt || 0).getTime();
    if (nextTime >= currentTime) {
      latestByProject.set(projectId, deployment);
    }
  });

  return [...latestByProject.values()];
};

const resolveDeployHref = (deployment) => {
  if (deployment.deployUrl) {
    return deployment.deployUrl;
  }

  if (!deployment.domain) {
    return '';
  }

  return deployment.domain.startsWith('http') ? deployment.domain : `https://${deployment.domain}`;
};

const resolvePublishedAt = (deployment) =>
  deployment.builtAtUtc || deployment.updatedAt || deployment.createdAt || null;

const statusTone = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'running' || normalized === 'active') {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300';
  }
  if (normalized === 'stopped' || normalized === 'completed') {
    return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
  }
  if (normalized === 'failed') {
    return 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300';
  }
  return 'bg-sky-50 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300';
};

const statusLabel = (status, t, { product = false } = {}) => {
  const normalized = (status || '').toLowerCase();
  if (product) {
    if (normalized === 'active') {
      return t('deploymentsStatusActive');
    }
    if (normalized === 'completed') {
      return t('deploymentsStatusCompleted');
    }
    if (normalized === 'failed') {
      return t('deploymentsStatusFailed');
    }
  }

  if (normalized === 'active') {
    return t('readyAppsStatusRunning');
  }

  const key = `readyAppsStatus${(status || '').charAt(0).toUpperCase()}${(status || '').slice(1)}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

export const ActiveCloudDeployments = ({ onOpenCatalog }) => {
  const dispatch = useAppDispatch();
  const { t, language, formatDate } = useI18n();
  const entitlement = useMyPackage();
  const projects = useAppSelector((state) => state.projects.items);
  const [deployments, setDeployments] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [deactivatingId, setDeactivatingId] = useState('');
  const [deactivateError, setDeactivateError] = useState('');
  const [appBusyId, setAppBusyId] = useState('');
  const [appActionError, setAppActionError] = useState('');
  const [historyTarget, setHistoryTarget] = useState(null);

  const projectNameById = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      map.set(project.id, project.name || project.title || project.id);
    });
    return map;
  }, [projects]);

  const deploymentsByProject = useMemo(() => {
    const map = new Map();
    deployments.forEach((deployment) => {
      if (!deployment.projectId) {
        return;
      }

      const list = map.get(deployment.projectId) || [];
      list.push(deployment);
      map.set(deployment.projectId, list);
    });
    return map;
  }, [deployments]);

  const liveItems = useMemo(() => {
    const products = latestDeploymentPerProduct(deployments).map((deployment) => ({
      kind: 'product',
      id: deployment.id,
      sortAt: resolvePublishedAt(deployment),
      deployment
    }));

    const apps = instances.map((instance) => ({
      kind: 'app',
      id: instance.id,
      sortAt: instance.updatedAt || instance.createdAt,
      instance
    }));

    return [...products, ...apps].sort((a, b) => {
      const aTime = new Date(a.sortAt || 0).getTime();
      const bTime = new Date(b.sortAt || 0).getTime();
      return bTime - aTime;
    });
  }, [deployments, instances]);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');

    try {
      const [nextDeployments, nextInstances] = await Promise.all([
        fetchDeploymentsApi(),
        fetchMarketplaceAppsApi()
      ]);
      setDeployments(nextDeployments);
      setInstances(nextInstances);
    } catch (error) {
      setListError(error?.message || t('deploymentsListError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const shouldPollApps = instances.some((instance) =>
    ['creating', 'deleting'].includes((instance.status || '').toLowerCase())
  );

  useEffect(() => {
    if (!shouldPollApps) {
      return undefined;
    }

    const timer = window.setInterval(async () => {
      try {
        const nextInstances = await fetchMarketplaceAppsApi();
        setInstances(nextInstances);
      } catch {
        // Keep last known list while Coolify is still starting the app.
      }
    }, 5000);

    return () => window.clearInterval(timer);
  }, [shouldPollApps]);

  const handleDeactivate = async (deploymentId) => {
    if (!deploymentId || deactivatingId) {
      return;
    }

    setDeactivatingId(deploymentId);
    setDeactivateError('');

    try {
      await completeDeploymentApi({ deploymentId });
      await refreshMyPackage();
      setDeployments((current) =>
        current.map((item) =>
          item.id === deploymentId ? { ...item, status: 'Completed' } : item
        )
      );
    } catch (error) {
      setDeactivateError(error?.message || t('deploymentsDeactivateError'));
    } finally {
      setDeactivatingId('');
    }
  };

  const handleAppPower = async (instance) => {
    if (!instance?.id || appBusyId) {
      return;
    }

    const isRunning = (instance.status || '').toLowerCase() === 'running';
    setAppBusyId(instance.id);
    setAppActionError('');
    try {
      const next = isRunning
        ? await stopMarketplaceAppApi(instance.id)
        : await startMarketplaceAppApi(instance.id);
      await refreshMyPackage();
      setInstances((current) => current.map((item) => (item.id === next.id ? next : item)));
    } catch (error) {
      setAppActionError(error?.message || t('readyAppsActionError'));
    } finally {
      setAppBusyId('');
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((item) => (
          <div
            key={item}
            className="h-[88px] animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900"
          />
        ))}
      </div>
    );
  }

  if (listError) {
    return (
      <div className="space-y-3 rounded-2xl border border-rose-200 bg-rose-50/80 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
        <p>{listError}</p>
        <Button type="button" variant="secondary" className="px-3 py-2" onClick={load}>
          {t('deploymentsRetry')}
        </Button>
      </div>
    );
  }

  if (liveItems.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-10 text-center dark:border-neutral-800 dark:bg-neutral-950">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
          <CloudOff size={20} />
        </div>
        <h3 className="text-base font-semibold text-neutral-950 dark:text-white">{t('cloudLiveEmptyTitle')}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-500">{t('cloudLiveEmptyBody', { suffix: READY_APP_DOMAIN_SUFFIX })}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 rounded-lg bg-neutral-950 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
          >
            <Rocket size={16} />
            {t('cloudLiveEmptyProductsCta')}
          </Link>
          <Button type="button" variant="secondary" onClick={onOpenCatalog}>
            <AppWindow size={16} />
            {t('cloudLiveEmptyAppsCta')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {deactivateError || appActionError ? (
        <p className="rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
          {deactivateError || appActionError}
        </p>
      ) : null}

      {liveItems.map((item) => {
        if (item.kind === 'app') {
          const instance = item.instance;
          const title = readyAppDisplayTitle(instance, language);
          const isRunning = (instance.status || '').toLowerCase() === 'running';
          const isBusy = appBusyId === instance.id;
          const startNeedsSlot =
            !isRunning
            && !occupiesPublishSlot(instance.status)
            && Boolean(entitlement)
            && !hasPublishSlotRemaining(entitlement);

          return (
            <article
              key={`app-${instance.id}`}
              className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
                      <AppWindow size={12} />
                      {t('cloudKindReadyApp')}
                    </span>
                    <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', statusTone(instance.status))}>
                      {statusLabel(instance.status, t)}
                    </span>
                  </div>
                  <h3 className="mt-2 truncate text-sm font-semibold text-neutral-950 dark:text-white">{title}</h3>
                  <p className="mt-1 truncate text-sm text-neutral-500">{instance.fqdn}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {instance.url ? (
                    <a href={instance.url} target="_blank" rel="noreferrer">
                      <Button type="button" variant="secondary" className="px-3 py-2">
                        <ExternalLink size={16} />
                        {t('cloudOpenSite')}
                      </Button>
                    </a>
                  ) : null}
                  <Link to={`/apps/${instance.id}`}>
                    <Button type="button" variant="secondary" className="px-3 py-2">
                      <Settings2 size={16} />
                      {t('cloudManageApp')}
                    </Button>
                  </Link>
                  <Button
                    type="button"
                    variant={isRunning ? 'secondary' : 'primary'}
                    className="px-3 py-2"
                    disabled={isBusy || startNeedsSlot}
                    title={startNeedsSlot ? t('readyAppsStartNeedsSlot') : undefined}
                    onClick={() => handleAppPower(instance)}
                  >
                    {isBusy ? <LoaderCircle size={16} className="animate-spin" /> : isRunning ? <PowerOff size={16} /> : <Power size={16} />}
                    {isRunning ? t('readyAppsStop') : t('readyAppsStart')}
                  </Button>
                </div>
              </div>
            </article>
          );
        }

        const deployment = item.deployment;
        const projectName = projectNameById.get(deployment.projectId) || deployment.projectId || '—';
        const publishedAt = resolvePublishedAt(deployment);
        const href = resolveDeployHref(deployment);
        const isActive = isActiveDeployment(deployment);
        const isDeactivating = deactivatingId === deployment.id;
        const history = deploymentsByProject.get(deployment.projectId) || [];

        return (
          <article
            key={`product-${deployment.id}`}
            className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
                    <FolderKanban size={12} />
                    {t('cloudKindProduct')}
                  </span>
                  <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', statusTone(deployment.status))}>
                    {statusLabel(deployment.status, t, { product: true })}
                  </span>
                </div>
                <Link
                  to={`/workspace/${deployment.projectId}`}
                  className="mt-2 block truncate text-sm font-semibold text-neutral-950 transition hover:text-neutral-600 dark:text-white dark:hover:text-neutral-300"
                  title={t('cloudOpenProject')}
                >
                  {projectName}
                </Link>
                <p className="mt-1 truncate text-sm text-neutral-500">
                  {publishedAt ? formatDate(publishedAt, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  {deployment.domain ? (
                    <span dir="ltr" className="ms-1">
                      · {deployment.domain}
                    </span>
                  ) : null}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                {isActive && href ? (
                  <a href={href} target="_blank" rel="noreferrer">
                    <Button type="button" variant="secondary" className="px-3 py-2">
                      <ExternalLink size={16} />
                      {t('cloudOpenSite')}
                    </Button>
                  </a>
                ) : null}
                <Link to={`/workspace/${deployment.projectId}`}>
                  <Button type="button" variant="secondary" className="px-3 py-2">
                    <Settings2 size={16} />
                    {t('cloudOpenProject')}
                  </Button>
                </Link>
                {history.length > 1 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="px-3 py-2"
                    onClick={() =>
                      setHistoryTarget({
                        projectId: deployment.projectId,
                        name: projectName,
                        currentId: deployment.id
                      })
                    }
                  >
                    <History size={16} />
                    {t('cloudPreviousDeployments')}
                  </Button>
                ) : null}
                {isActive ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="px-3 py-2"
                    disabled={Boolean(deactivatingId)}
                    onClick={() => handleDeactivate(deployment.id)}
                  >
                    {isDeactivating ? <LoaderCircle size={16} className="animate-spin" /> : <PowerOff size={16} />}
                    {isDeactivating ? t('deploymentsDeactivating') : t('deploymentsDeactivate')}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
        );
      })}

      <ProductDeploymentHistoryModal
        open={Boolean(historyTarget)}
        onClose={() => setHistoryTarget(null)}
        projectName={historyTarget?.name || ''}
        currentDeploymentId={historyTarget?.currentId || ''}
        deployments={historyTarget ? deploymentsByProject.get(historyTarget.projectId) || [] : []}
        statusTone={statusTone}
        statusLabel={statusLabel}
        t={t}
        formatDate={formatDate}
      />
    </div>
  );
};
